// PATH: src/components/modules/devtools/panels/ErrorLogPanel.js

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
} from "@mui/material";

/**
 * ErrorLogPanel
 *
 * Lightweight viewer for dev_ide_error_log:
 * - Filters (severity, source, module, resolved, search)
 * - Left: list of error entries
 * - Right: details for the selected entry + "Mark as resolved"
 */

const severityOptions = [
  { value: "ALL", label: "All severities" },
  { value: "INFO", label: "Info" },
  { value: "WARN", label: "Warn" },
  { value: "ERROR", label: "Error" },
  { value: "FATAL", label: "Fatal" },
];

const resolvedOptions = [
  { value: "ALL", label: "All" },
  { value: "0", label: "Unresolved" },
  { value: "1", label: "Resolved" },
];

const ErrorLogPanel = ({ currentUsername }) => {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [severityFilter, setSeverityFilter] = useState("ALL");
  const [sourceFilter, setSourceFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [resolvedFilter, setResolvedFilter] = useState("0"); // default: unresolved
  const [search, setSearch] = useState("");

  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);
  const [resolveMessage, setResolveMessage] = useState(null);

  const selectedItem = useMemo(
    () => items.find((row) => row.id === selectedId) || null,
    [items, selectedId]
  );

  const loadErrors = async () => {
    setLoading(true);
    setLoadError(null);
    setResolveMessage(null);

    try {
      const params = new URLSearchParams();

      if (severityFilter !== "ALL") {
        params.append("severity", severityFilter);
      }
      if (sourceFilter.trim()) {
        params.append("source", sourceFilter.trim());
      }
      if (moduleFilter.trim()) {
        params.append("module", moduleFilter.trim());
      }
      if (resolvedFilter !== "ALL") {
        params.append("resolved", resolvedFilter);
      }
      if (search.trim()) {
        params.append("search", search.trim());
      }

      // Reasonable default limit
      params.append("limit", "200");

      const res = await axios.get(`/api/dev-ide/error-logs?${params.toString()}`);

      if (!res.data || !res.data.success) {
        throw new Error(res.data && res.data.error ? res.data.error : "Unknown error");
      }

      const rows = res.data.items || [];
      setItems(rows);

      // Auto-select first row if none selected
      if (rows.length > 0 && !rows.find((r) => r.id === selectedId)) {
        setSelectedId(rows[0].id);
      }
      if (rows.length === 0) {
        setSelectedId(null);
      }
    } catch (err) {
      setLoadError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Auto-load on mount and whenever filters change
    loadErrors();
  }, [severityFilter, sourceFilter, moduleFilter, resolvedFilter, search]);

  const handleResolve = async () => {
    if (!selectedItem) return;

    setResolving(true);
    setResolveError(null);
    setResolveMessage(null);

    try {
      const payload = {
        id: selectedItem.id,
        resolved_by: currentUsername || "system",
      };

      const res = await axios.post("/api/dev-ide/error-logs/resolve", payload);

      if (!res.data || !res.data.success) {
        throw new Error(res.data && res.data.error ? res.data.error : "Unknown error");
      }

      setResolveMessage("Marked as resolved.");

      // Refresh list so the resolved flag & timestamp are up to date
      await loadErrors();
    } catch (err) {
      setResolveError(err.message || String(err));
    } finally {
      setResolving(false);
    }
  };

  const renderDetails = () => {
    if (!selectedItem) {
      return (
        <Typography variant="body1" color="text.secondary">
          Select an error from the list to view details.
        </Typography>
      );
    }

    const {
      id,
      created_at,
      source,
      severity,
      module,
      file_path,
      change_id,
      username,
      message,
      details,
      context_json,
      resolved,
      resolved_at,
      resolved_by,
    } = selectedItem;

    let prettyContext = context_json;
    try {
      if (context_json) {
        const parsed = JSON.parse(context_json);
        prettyContext = JSON.stringify(parsed, null, 2);
      }
    } catch (e) {
      // If JSON parse fails, just show raw context_json
      prettyContext = context_json;
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {message || "(no message)"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ID #{id} • {created_at} • {source} • {severity}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {module && <>Module: {module} &nbsp;&nbsp;</>}
            {file_path && <>File: {file_path} &nbsp;&nbsp;</>}
            {change_id && <>Change: {change_id} &nbsp;&nbsp;</>}
            {username && <>User: {username}</>}
          </Typography>
          <Typography
            variant="body2"
            color={resolved ? "success.main" : "warning.main"}
            sx={{ mt: 1 }}
          >
            {resolved
              ? `Resolved at ${resolved_at || ""} by ${resolved_by || "unknown"}`
              : "Unresolved"}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Details
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              mb: 2,
              maxHeight: 260,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              fontSize: "0.8rem",
            }}
          >
            {details || "(no details)"}
          </Paper>

          <Typography variant="subtitle2" gutterBottom>
            Context
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              maxHeight: 200,
              overflow: "auto",
              whiteSpace: "pre",
              fontFamily: "monospace",
              fontSize: "0.8rem",
            }}
          >
            {prettyContext || "(no context)"}
          </Paper>
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleResolve}
            disabled={!selectedItem || resolving || selectedItem.resolved}
          >
            {selectedItem && selectedItem.resolved
              ? "Already resolved"
              : resolving
              ? "Resolving..."
              : "Mark as resolved"}
          </Button>
          {resolveMessage && (
            <Typography variant="body2" color="success.main">
              {resolveMessage}
            </Typography>
          )}
          {resolveError && (
            <Typography variant="body2" color="error">
              {resolveError}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Error Log
      </Typography>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel id="error-log-severity-label">Severity</InputLabel>
            <Select
              labelId="error-log-severity-label"
              label="Severity"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
            >
              {severityOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Source"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            placeholder="e.g. FRONTEND_REBUILD"
          />

          <TextField
            size="small"
            label="Module"
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            placeholder="e.g. devtools"
          />

          <FormControl sx={{ minWidth: 140 }} size="small">
            <InputLabel id="error-log-resolved-label">Resolved</InputLabel>
            <Select
              labelId="error-log-resolved-label"
              label="Resolved"
              value={resolvedFilter}
              onChange={(e) => setResolvedFilter(e.target.value)}
            >
              {resolvedOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search message/details"
            sx={{ flex: 1, minWidth: 160 }}
          />

          <Button
            variant="outlined"
            size="small"
            onClick={loadErrors}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </Button>
        </Stack>

        {loadError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {loadError}
          </Typography>
        )}
      </Paper>

      {/* Main content: list + details */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 300, gap: 2 }}>
        {/* Left: list */}
        <Paper
          variant="outlined"
          sx={{
            width: "35%",
            minWidth: 260,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 1,
              borderBottom: "1px solid rgba(0,0,0,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="subtitle2">Entries</Typography>
            {loading && <CircularProgress size={16} />}
          </Box>
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {items.length === 0 && !loading ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ p: 1 }}
              >
                No error entries found with current filters.
              </Typography>
            ) : (
              <List dense>
                {items.map((row) => (
                  <React.Fragment key={row.id}>
                    <ListItemButton
                      selected={row.id === selectedId}
                      onClick={() => setSelectedId(row.id)}
                      alignItems="flex-start"
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontWeight: row.resolved ? 400 : 600,
                            }}
                          >
                            {row.message || "(no message)"}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {row.created_at} • {row.source} • {row.severity}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Paper>

        {/* Right: details */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: 2,
            minWidth: 0,
          }}
        >
          {renderDetails()}
        </Paper>
      </Box>
    </Box>
  );
};

export default ErrorLogPanel;